import { metersPerSecondsToKphOrMph, secondsToMinuteClock } from "refsix-core";
import { TFunction } from "react-i18next";
import {
    EventName,
    GoalEvent,
    HalfEvent,
    IEventMap,
    MatchEvent,
    SelectedTeam,
} from "refsix-js-models";
import { ReplayMatchEvent } from "./replayTypes";
import { DisplayEvent } from "../../models/DisplayEvent";

/**
 * Display formatters ported from the legacy Angular video replay tool
 * (MatchTimeFilter, SecondsToClockFilter, SpeedMSToKmHFilter, HeartRateFilter).
 */

export interface MatchTimeEvent {
    offset: number; // millis since the start of the current half
    minOfHalf: number; // millis of play already accumulated before this half (e.g. 45*60*1000)
}

/**
 * Converts seconds to an hh:mm:ss clock used by the calibration "start time" display.
 */
export function secondsToClock(secs: number): string {
    if (isNaN(secs)) {
        return "00:00:00";
    }
    const hrs = Math.floor(secs / 3600);
    const mins = Math.floor((secs - hrs * 3600) / 60);
    const seconds = Math.round(secs - hrs * 3600 - mins * 60);
    const pad = (n: number) => ("0" + n).slice(-2);
    return `${pad(hrs)}:${pad(mins)}:${pad(seconds)}`;
}

/**
 * Returns the prescribed length of a playing segment in seconds, for use as the
 * `lengthSecs` argument to {@link matchTime}.
 *
 * The segments come from `getPlayingSegments`, which has already converted
 * `HalfEvent.length` from millis to MINUTES, so we only multiply by 60 here
 * (defaulting to a 45-minute half). Passing the raw `length` straight to
 * matchTime treats minutes as millis and makes the overlay jump into added time
 * almost immediately.
 */
export function playingSegmentLengthSecs(half?: HalfEvent): number {
    return (half?.length || 45) * 60;
}

/**
 * Formats the on-video match time overlay, e.g. "45:00" or "45:00 +2:13" when in
 * added time.
 */
export function matchTime(event: MatchTimeEvent, lengthSecs: number): string {
    if (!event || isNaN(event.offset)) {
        return "00:00";
    }

    const offsetSecs = event.offset / 1000;
    const minOfHalfSecs = (event.minOfHalf || 0) / 1000;
    const secs = offsetSecs + minOfHalfSecs;
    const totalLength = lengthSecs + minOfHalfSecs;

    if (secs >= totalLength) {
        const additionalSecs = secs - totalLength;
        return (
            secondsToMinuteClock(totalLength) +
            " +" +
            secondsToMinuteClock(additionalSecs)
        );
    }
    return secondsToMinuteClock(secs);
}

/**
 * Formats a speed in m/s as a localised km/h or mph value, with an optional unit suffix.
 */
export function speedMSToKmH(
    speed: number,
    useImperial: boolean,
    hideUnit?: boolean
): string {
    const suffix = hideUnit ? "" : useImperial ? " mph" : " kmph";
    if (isNaN(speed)) {
        return "0" + suffix;
    }
    return metersPerSecondsToKphOrMph(speed, useImperial) + suffix;
}

/**
 * Decorates the match events with the playback offsets the replay tool needs to
 * seek to them, and relabels half-time / break events as "End of half".
 * Ported from the legacy ReplayController.processMatchEvents.
 */
export function processReplayEvents(
    matchEvents: IEventMap | undefined
): ReplayMatchEvent[] {
    if (!matchEvents) {
        return [];
    }
    // Shallow-copy each event so the offsets/relabelling we apply below don't
    // mutate the shared Redux/Pouch-backed match events (this runs memoised).
    const events = (Object.values(matchEvents) as ReplayMatchEvent[])
        .map((event) => ({ ...event }))
        .sort((a, b) => a.timestamp - b.timestamp);

    let currentHalf = -1;
    let minOfHalf = 0;
    let startTs = 0;
    let endOfCurrentHalf = 0;

    events.forEach((event) => {
        const halfEvent = event as ReplayMatchEvent & {
            playing?: boolean;
            minuteOfPlay?: number;
        };
        if (event.eventName === EventName.half && halfEvent.playing === true) {
            currentHalf++;
            startTs = event.timestamp;
            minOfHalf = halfEvent.minuteOfPlay || 0;
            endOfCurrentHalf = halfEvent.endTime || event.timestamp;
        } else if (
            event.name &&
            (event.name.indexOf("Half Time") !== -1 ||
                event.name.indexOf("Break") !== -1)
        ) {
            event.name = "End of half";
            event.timestamp = endOfCurrentHalf;
        }
        event.offset = event.timestamp - startTs;
        event.segmentIndex = currentHalf;
        event.minOfHalf = minOfHalf * 60 * 1000;
    });

    return events;
}

/**
 * Returns just the goal events in chronological order, used by the live score overlay.
 */
export function goalEventsSorted(events: MatchEvent[]): MatchEvent[] {
    return events
        .filter((event) => event.eventName === EventName.goal)
        .sort((a, b) => a.timestamp - b.timestamp);
}

export interface LiveScore {
    home: number;
    away: number;
}

/**
 * Tallies the goals scored up to (and including) the given timestamp for the
 * live score overlay. Expects `goalEvents` sorted ascending by timestamp (see
 * goalEventsSorted) so it can stop once it passes the playhead. Goals without a
 * recognised team side are skipped rather than aborting the tally.
 */
export function liveScore(
    goalEvents: GoalEvent[],
    currentTimestamp: number
): LiveScore {
    const result: LiveScore = { home: 0, away: 0 };
    for (const goal of goalEvents) {
        if (currentTimestamp < goal.timestamp) {
            break;
        }
        const side = goal.team?.side;
        if (side === SelectedTeam.home || side === SelectedTeam.away) {
            result[side]++;
        }
    }
    return result;
}

export interface SeekTarget {
    position: number; // millis offset from the start of the playing segment
    segmentIndex: number; // index into the playing segments (matchTimings)
}

/**
 * Resolves the video seek target for a tapped match-log row.
 *
 * Each `DisplayEvent` carries its own absolute timestamp (`sortingTs` — the half
 * end time for an end-of-half row, the event timestamp otherwise) and the
 * `period` HalfEvent it belongs to. We locate the playing segment by matching
 * that period's start timestamp against `matchTimings` (the playing segments)
 * and derive the in-half offset from the timestamp. End-of-half rows therefore
 * seek to the end of the half automatically.
 *
 * Returns null for rows that aren't in a playable segment (e.g. half-time /
 * break rows, or events before kick-off).
 */
export function resolveSeekTarget(
    displayEvent: DisplayEvent,
    matchTimings: HalfEvent[]
): SeekTarget | null {
    const period = displayEvent.period;
    if (!period) {
        return null;
    }
    const segmentIndex = matchTimings.findIndex(
        (segment) => segment.timestamp === period.timestamp
    );
    if (segmentIndex < 0) {
        return null;
    }
    const position = Math.max(0, displayEvent.sortingTs - period.timestamp);
    return { position, segmentIndex };
}

/**
 * Builds the overlay text shown during half-time / full-time / pre-kickoff in the
 * video player, given the calibrated play segments and the current video time.
 */
export function overlayTimeDuringBreak(
    playSegmentOffsets: { offsetSec: number }[],
    currentVideoTime: number,
    t: TFunction
): string {
    if (!playSegmentOffsets.length) {
        return "00:00";
    }
    if (playSegmentOffsets[0].offsetSec > currentVideoTime) {
        return "00:00";
    }
    if (
        playSegmentOffsets[playSegmentOffsets.length - 1].offsetSec <
        currentVideoTime
    ) {
        return t("performance.VideoPlayer.fullTime");
    }
    return t("performance.VideoPlayer.halfTime");
}
